/**
 * LG ThinQ appliance display (washer / dryer / dishwasher).
 *
 * Renders into the Sensors panel alongside the Govee cards — a laundry cycle
 * reads as just another sensor. Reads the state the cron poll stored, so this
 * never talks to LG directly.
 */

import { API_ENDPOINTS } from '../config.js';
import { setLastCheck } from './state.js';
import { updateHealthStatus } from './updates.js';

const KIND_ICONS = {
  washer: '🌀',
  dryer: '🔥',
  dishwasher: '🍽️',
  styler: '👔',
};

// Latest poll, keyed by device id, so a card click can render the detail modal
// without waiting on a fresh request.
const applianceCache = {};

/**
 * The status the card should present.
 *
 * A dishwasher full of clean dishes powers itself off, so its raw status drops
 * to idle while the load is still sitting there. The needs-emptying flag
 * outranks the run state for display purposes.
 * @param {object} appliance - Normalised appliance entry
 * @returns {string} Status key for labels and styling
 */
export function effectiveStatus(appliance) {
  return appliance.needsEmptying ? 'needsEmptying' : appliance.status;
}

/**
 * Format a minute count as a compact duration.
 * @param {number|null} minutes - Minutes remaining
 * @returns {string} Formatted duration, or empty string when unknown
 */
export function formatRemaining(minutes) {
  if (minutes === null || minutes === undefined || minutes < 0) {
    return '';
  }

  if (minutes < 60) {
    return `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;

  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}

/**
 * Format how long ago a unix timestamp was, as compactly as possible.
 *
 * Deliberately terse: the whole card is one line on a fixed-height panel, so
 * "45m" has to do the work of "45 minutes ago".
 * @param {number|null} timestamp - Unix timestamp in seconds
 * @param {number} [nowMs] - Current time in ms, injectable for tests
 * @returns {string} Compact elapsed time, or empty string
 */
export function formatSince(timestamp, nowMs = Date.now()) {
  if (!timestamp) {
    return '';
  }

  const seconds = Math.floor(nowMs / 1000) - timestamp;

  if (seconds < 60) {
    return 'now';
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h`;
  }

  return `${Math.floor(hours / 24)}d`;
}

/**
 * Build the single phrase describing what the appliance is doing.
 *
 * This carries the whole state on one line — there is no separate status word,
 * because on a quarter-width card "Running" next to "53m left" said the same
 * thing twice and cost a row the panel does not have.
 * @param {object} appliance - Normalised appliance entry from the API
 * @param {number} [nowMs] - Current time in ms, injectable for tests
 * @returns {string} Summary text for the card
 */
export function describeAppliance(appliance, nowMs = Date.now()) {
  const elapsed = () => formatSince(appliance.completedAt || appliance.since, nowMs);

  if (appliance.needsEmptying) {
    const waiting = elapsed();
    return waiting ? `Needs emptying · ${waiting}` : 'Needs emptying';
  }

  switch (appliance.status) {
  case 'running': {
    const remaining = formatRemaining(appliance.remainingMinutes);
    return remaining ? `${remaining} left` : 'Running';
  }
  case 'finished':
    // A dryer only tumbles in WRINKLE_CARE because the load is still inside,
    // which is the closest thing to a door sensor it has.
    if (appliance.runState === 'WRINKLE_CARE') {
      return `Still inside · ${elapsed()}`;
    }

    // completedAt is latched on entry into the finished group, so this keeps
    // counting up through the follow-on states.
    return `Done · ${elapsed()}`;
  case 'paused':
    return 'Paused';
  case 'scheduled': {
    const remaining = formatRemaining(appliance.remainingMinutes);
    return remaining ? `Starts in ${remaining}` : 'Delayed start';
  }
  case 'error':
    return 'Check appliance';
  case 'offline':
    return 'Offline';
  default:
    if (appliance.doorOpen) {
      return 'Door open';
    }

    // Rinse aid rides along here rather than as its own badge, which would
    // otherwise cost the card a second row.
    return appliance.rinseRefillNeeded ? 'Ready · rinse aid' : 'Ready';
  }
}

/**
 * Render one appliance card.
 *
 * Laid out as a short horizontal strip rather than a tall stack: these sit
 * below the Govee cards on a fixed-height tablet panel, and anything taller
 * pushes the sensor grid into a scroll the kiosk can never perform.
 * @param {object} appliance - Normalised appliance entry
 * @returns {string} Card HTML
 */
function renderCard(appliance) {
  const icon = KIND_ICONS[appliance.kind] || '⚙️';
  const status = effectiveStatus(appliance);
  const summary = describeAppliance(appliance);

  let progressBar = '';
  if (appliance.status === 'running' && appliance.progress !== null) {
    progressBar = `<div class="appliance-progress">
        <div class="appliance-progress-fill" style="width: ${appliance.progress}%"></div>
      </div>`;
  }

  return `
    <div class="cell appliance-holder appliance-${status}" data-device-id="${appliance.deviceId}" data-open="modal-appliance-detail">
      <div class="appliance-row">
        <span class="appliance-icon">${icon}</span>
        <span class="appliance-name">${appliance.name}</span>
        <span class="appliance-status">${summary}</span>
      </div>
      ${progressBar}
    </div>`;
}

/**
 * Turn a unix timestamp into a readable local time.
 * @param {number|null} timestamp - Unix timestamp in seconds
 * @returns {string} Localised time, or an em dash when absent
 */
function formatClock(timestamp) {
  return timestamp ? new Date(timestamp * 1000).toLocaleTimeString() : '—';
}

/**
 * Flatten LG's nested state object into displayable rows.
 *
 * The shape varies by appliance and LG adds fields over time, so this walks
 * whatever arrived rather than naming keys.
 * @param {object} raw - Raw state object from the ThinQ API
 * @param {string} [prefix] - Parent key path, used when recursing
 * @returns {Array<{key: string, value: string}>} Flattened rows
 */
export function flattenRawState(raw, prefix = '') {
  if (!raw || typeof raw !== 'object') {
    return [];
  }

  return Object.entries(raw).flatMap(([key, value]) => {
    const path = prefix ? `${prefix}.${key}` : key;

    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      return flattenRawState(value, path);
    }

    return [{ key: path, value: String(value) }];
  });
}

/**
 * Build the detail modal contents for one appliance.
 * @param {object} appliance - Normalised appliance entry
 * @param {number} [nowMs] - Current time in ms, injectable for tests
 * @returns {string} Modal HTML
 */
export function renderApplianceDetail(appliance, nowMs = Date.now()) {
  const icon = KIND_ICONS[appliance.kind] || '⚙️';

  // Only rows with something to say; a dryer has no door or rinse aid, and
  // showing "—" for half the table is noise.
  const rows = [
    ['State', describeAppliance(appliance, nowMs)],
    ['Run state', appliance.runState || '—'],
    ['Time remaining', formatRemaining(appliance.remainingMinutes) || '—'],
    ['Cycle length', formatRemaining(appliance.totalMinutes) || '—'],
    ['Progress', appliance.progress === null || appliance.progress === undefined ? '—' : `${appliance.progress}%`],
    ['Door', appliance.hasDoorSensor ? (appliance.doorOpen ? 'Open' : 'Closed') : null],
    ['Needs emptying', appliance.hasDoorSensor ? (appliance.needsEmptying ? 'Yes' : 'No') : null],
    ['Rinse aid', appliance.rinseRefillNeeded === null || appliance.rinseRefillNeeded === undefined
      ? null
      : (appliance.rinseRefillNeeded ? 'Refill needed' : 'OK')],
    ['Cycles run', appliance.cycleCount === null || appliance.cycleCount === undefined ? null : String(appliance.cycleCount)],
    ['Remote control', appliance.remoteControlEnabled === null || appliance.remoteControlEnabled === undefined
      ? null
      : (appliance.remoteControlEnabled ? 'Enabled' : 'Disabled')],
    ['Finished at', appliance.completedAt ? formatClock(appliance.completedAt) : null],
    ['Model', appliance.model || null],
    ['Device type', appliance.type || null],
    ['Last polled', formatClock(appliance.updatedAt)],
  ].filter(([, value]) => value !== null);

  const summaryRows = rows
    .map(([label, value]) => `<tr><td>${label}</td><td>${value}</td></tr>`)
    .join('');

  const rawRows = flattenRawState(appliance.raw)
    .map(({ key, value }) => `<tr><td><code>${key}</code></td><td>${value}</td></tr>`)
    .join('');

  const rawSection = rawRows
    ? `<h6 class="appliance-detail-heading">Raw ThinQ state</h6>
       <table class="appliance-detail-table appliance-detail-raw"><tbody>${rawRows}</tbody></table>`
    : '';

  return `
    <h4 class="appliance-detail-title">${icon} ${appliance.name}</h4>
    <table class="appliance-detail-table"><tbody>${summaryRows}</tbody></table>
    ${rawSection}`;
}

/**
 * Populate and show the detail modal for a device.
 * @param {string} deviceId - LG device id from the clicked card
 */
export function showApplianceDetail(deviceId) {
  const appliance = applianceCache[deviceId];

  if (!appliance) {
    return;
  }

  // Remembered so the next poll can refresh the modal in place.
  jQuery('#modal-appliance-detail').data('device-id', deviceId);
  jQuery('#appliance-detail').html(renderApplianceDetail(appliance));
}

/**
 * Fetch appliance state and refresh the panel.
 */
export function updateAppliances() {
  jQuery('#wsStatusAppliances').removeClass('success alert').addClass('secondary');

  jQuery.getJSON(API_ENDPOINTS.APPLIANCES, {}, (data) => {
    const appliances = data.appliances || [];

    // Refresh the lookup the detail modal reads from, dropping devices that
    // are no longer reported.
    Object.keys(applianceCache).forEach((id) => delete applianceCache[id]);
    appliances.forEach((appliance) => {
      applianceCache[appliance.deviceId] = appliance;
    });

    // Cards sit alongside the Govee sensors, so an empty response should leave
    // no trace rather than push a placeholder into the sensor grid.
    jQuery('#appliance-list').html(appliances.map(renderCard).join(''));

    // Keep an open modal in step with the poll it is showing.
    const openId = jQuery('#modal-appliance-detail').data('device-id');
    if (openId && applianceCache[openId]) {
      jQuery('#appliance-detail').html(renderApplianceDetail(applianceCache[openId]));
    }

    jQuery('#wsUpdateAppliances').text(new Date().toLocaleTimeString());
    jQuery('#wsStatusAppliances').removeClass('secondary alert').addClass('success');
    setLastCheck('Appliances', new Date());
    setTimeout(() => updateHealthStatus(), 50);
  }).fail((jqXHR, textStatus, errorThrown) => {
    console.error('[Appliances] Failed to update appliances:', textStatus, errorThrown);
    jQuery('#wsStatusAppliances').removeClass('secondary success').addClass('alert');
    setTimeout(() => updateHealthStatus(), 50);
  });
}
